import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { createBooking } from "../services/api";
import { ConfirmationModalProps } from "../types";

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  bookingData,
  selectedStudios,
  selectedSlots,
  onComplete,
  onClose,
  onEdit,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await createBooking(bookingData);
      setSuccess(true);
      // Scroll to top when booking is confirmed
      window.scrollTo({ top: 0, behavior: "smooth" });
      // If in an iframe, also notify parent window to scroll page to top (to show header)
      if (window.parent !== window) {
        window.parent.postMessage(
          {
            type: "iframe-scroll-to-top",
          },
          "*" // In production, replace '*' with the specific origin
        );
      }
    } catch (err: any) {
      setError(err.message || "Failed to create booking");
      console.error("Booking error:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date: string): string => {
    return format(parseISO(date), "h:mm a");
  };

  const formatDate = (date: string): string => {
    return format(parseISO(date), "MMM d, yyyy");
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6 text-center">
          <div className="text-primary-600 text-4xl sm:text-6xl mb-4">✓</div>
          <h2 className="text-xl sm:text-2xl font-bold text-primary-900 mb-4">
            Booking Confirmed!
          </h2>
          <p className="text-sm sm:text-base text-primary-600 mb-6">
            Your studio booking has been successfully created. You will receive
            a confirmation email shortly.
          </p>
          <div className="space-y-3">
            <button
              onClick={onClose}
              className="w-full px-4 sm:px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-2xl font-bold text-primary-900">
              Confirm Your Booking
            </h2>
            <button
              onClick={onEdit}
              className="text-primary-400 hover:text-primary-600 transition-colors"
            >
              <svg
                className="w-5 h-5 sm:w-6 sm:h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-primary-50 border border-primary-400 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 text-primary-700 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-primary-800 font-medium text-sm sm:text-base">
                  Error:
                </span>
              </div>
              <p className="mt-1 text-primary-700 text-xs sm:text-sm">
                {error}
              </p>
            </div>
          )}

          <div className="space-y-4 sm:space-y-6">
            {/* Booking Summary */}
            <div className="bg-primary-50 rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-primary-900 mb-3 text-sm sm:text-base">
                Booking Summary
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <span className="text-primary-600">Project:</span>
                  <p className="font-medium">{bookingData.projectName}</p>
                </div>
                <div>
                  <span className="text-primary-600">Contact:</span>
                  <p className="font-medium">
                    {bookingData.firstName} {bookingData.lastName}
                  </p>
                </div>
                <div>
                  <span className="text-primary-600">Company:</span>
                  <p className="font-medium">{bookingData.company}</p>
                </div>
                <div>
                  <span className="text-primary-600">Email:</span>
                  <p className="font-medium">{bookingData.email}</p>
                </div>
                <div>
                  <span className="text-primary-600">Booking Types:</span>
                  <p className="font-medium">
                    {bookingData.bookingTypes.join(", ")}
                  </p>
                </div>
                {bookingData.maxHeadcount && (
                  <div>
                    <span className="text-primary-600">Max Headcount:</span>
                    <p className="font-medium">{bookingData.maxHeadcount}</p>
                  </div>
                )}
                <div>
                  <span className="text-primary-600">Studio:</span>
                  <p className="font-medium">
                    {selectedStudios.find(
                      (studio) => studio.id === bookingData.studio
                    )?.name || bookingData.studio}
                  </p>
                </div>
              </div>
            </div>

            {/* Selected Time Range */}
            {selectedSlots.length > 0 && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 sm:p-4">
                <h3 className="font-semibold text-primary-900 mb-2 text-sm sm:text-base">
                  Selected Time Range:
                </h3>
                <div className="text-sm sm:text-base text-primary-800">
                  {formatDate(selectedSlots[0].start)} -{" "}
                  {formatTime(selectedSlots[0].start)} to{" "}
                  {formatTime(selectedSlots[selectedSlots.length - 1].end)}
                  <span className="ml-2 text-primary-600">
                    (
                    {Math.ceil(
                      (new Date(
                        selectedSlots[selectedSlots.length - 1].end
                      ).getTime() -
                        new Date(selectedSlots[0].start).getTime()) /
                        (60 * 60 * 1000)
                    )}{" "}
                    hours)
                  </span>
                </div>
              </div>
            )}

            {/* Terms and Conditions */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold text-primary-800 mb-2 text-sm sm:text-base">
                Important Notes:
              </h4>
              <ul className="text-xs sm:text-sm text-primary-700 space-y-1">
                <li>
                  • This is just an inquiry and a team member will reach out to
                  confirm booking and details
                </li>
                <li>• Bookings are subject to availability confirmation</li>
                <li>
                  • Cancellations must be made at least 24 hours in advance
                </li>
                <li>
                  • Studio access will be provided 15 minutes before your
                  scheduled time
                </li>
                <li>
                  • Please arrive on time as late arrivals may result in reduced
                  booking time
                </li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-primary-200 space-y-3 sm:space-y-0">
            <button
              onClick={onEdit}
              disabled={loading}
              className="px-4 sm:px-6 py-3 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50 text-sm sm:text-base"
            >
              Edit Booking
            </button>

            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`px-6 sm:px-8 py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                loading
                  ? "bg-primary-300 text-primary-500 cursor-not-allowed"
                  : "bg-primary-600 text-white hover:bg-primary-700"
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Booking...
                </div>
              ) : (
                "Confirm Booking"
              )}
            </button>
          </div>

          {/* reCAPTCHA branding - required when hiding the badge */}
          <div className="mt-4 text-center text-xs text-gray-500">
            This site is protected by reCAPTCHA and the Google{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              Terms of Service
            </a>{" "}
            apply.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
